<template>
    <div class="flex items-center space-x-2 px-2 2xl:px-10 pt-2 text-sm">
        <span class="text-gray-500 mr-1">Quick filter:</span>
        <button type="button" @click="applyPresetLaptop"
            class="rounded px-2 py-0.5 border bg-dark-primary text-white hover:bg-dark-secondary">Laptops</button>
        <button type="button" @click="applyPresetAll"
            class="rounded px-2 py-0.5 border bg-gray-100 hover:bg-gray-200">All categories</button>
        <button type="button" @click="applyPresetReset"
            class="rounded px-2 py-0.5 border bg-gray-100 hover:bg-gray-200">Reset all filters</button>
    </div>
    <TablePage
        class="px-0 2xl:px-10 pb-2"
        :module-name="moduleName"
        :response="response"
        :isLoading="isLoading"
        :tableHeaders="tableHeaders"
        :filters="inventoryItemsFilter"
        :bulkOperations="inventoryItemsBulkOperations"
        :menuOptions="menuOptions"
        :default-ordering="['asset_name']"
        first-header-styling="!w-10"
        @refetch-results="loadInventoryItems"
        @clear-filter="clearFilter"
        @change-filters="setFilters"
        @set-filter-options="setFilterOptions"
    >
        <!-- <template #bulk-operations="{ events: {}}"> -->
            <!-- <CompanyCampaignChangeBulkOperations
                :bulkAction="bulkAction"
                :selectedResults="selectedResults"
                :campaigns="campaigns"
                @cancel-bulk-operation="hideBulkActionMenu"
                @close-bulk-operation="hideBulkOperations"
                @clear-selected-results="clearSelectAllClearSelectedResults"
                @refetch-results="refetchResults(fetchFilter)"
            /> -->
        <!-- </template> -->

        <template #row-form="{ events: { closeFormRow, refetchResults }, index, fetchFilter, userPermissions }">
            <InventoryItemRowForm
                :index="index"
                :userPermissions="userPermissions"
                :userOptions="inventoryItemsFilter.users.options"
                :inventory-category-options="inventoryItemsFilter.category.options"
                @refetch-results="refetchResults(fetchFilter)"
                @close-form="closeFormRow(index)"
            /> 
        </template>
        <template #table-row="{ events: { refetchResults }, selectAll, fetchFilter, userPermissions }">
            <InventoryItemRow
                v-for="inventoryItem in response.data.results"
                :key="inventoryItem.id"
                :inventory-item="inventoryItem"
                :inventory-category-options="inventoryItemsFilter.category.options"
                :selectAll="selectAll"
                :user-options="inventoryItemsFilter.users.options"
                :userPermissions="userPermissions"
                :is-loading="isLoading"
                @refetch-results="refetchResults(fetchFilter)"
                @delete-inventory-item="deleteItem"
                @loading="setLoading"
            />
        </template>
    </TablePage>
</template>

<script setup>
import { ref, reactive, } from 'vue';
import { useStore } from 'vuex';
import { getInventoryItems, getInventoryCategories, deleteInventoryItem } from '@/services/Inventory';
import TablePage from "@/components/base-components/TablePage";
import InventoryItemRow from './InventoryItemRow.vue';
import InventoryItemRowForm from './InventoryItemRowForm.vue';
import { useToast } from 'vue-toast-notification';
import { formatDate } from '@/composables/formatDate';
import { getUsers } from '@/services/User'

const store = useStore()
const $toast = useToast()
const isLoading = ref(false)
const moduleName = ref('Inventory Items')
const response = ref(null)
const tableHeaders = ref([
    {
        value: 'category_icon',
        label: '',
        class: 'p-0 w-10 whitespace-nowrap'
    },
    {
        value: 'asset_name',
        label: 'Device Name',
        class: 'p-0 w-24 whitespace-nowrap'
    },
    {
        value: 'old_device_name',
        label: 'Old Name',
        class: 'p-0 w-24 whitespace-nowrap'
    },
    {
        value: 'serial_number',
        label: 'Serial #',
        class: 'p-0 w-20 whitespace-nowrap'
    },
    {
        value: 'action1_installed',
        label: 'A1',
        class: 'p-0 w-14 whitespace-nowrap'
    },
    {
        value: 'action1_specs_captured',
        label: 'Specs',
        class: 'p-0 w-14 whitespace-nowrap'
    },
    {
        value: 'next_steps',
        label: 'Next Steps',
        class: 'p-0 w-56 whitespace-nowrap'
    },
    {
        value: 'status',
        label: 'Status',
        class: 'p-0 w-20 whitespace-nowrap'
    },
    {
        value: 'purpose',
        label: 'Purpose',
        class: 'p-0 w-24 whitespace-nowrap'
    },
    {
        value: 'issued_on',
        label: 'Issued To',
        class: 'p-0 w-40 whitespace-nowrap'
    },
    {
        value: 'brand',
        label: 'Brand',
        class: 'p-0 w-16 whitespace-nowrap'
    },
    {
        value: 'model',
        label: 'Model',
        class: 'p-0 w-36 whitespace-nowrap'
    },
    {
        value: 'notes',
        label: 'Notes',
        class: 'p-0 w-56 whitespace-nowrap'
    },
    {
        value: 'cost',
        label: 'Cost',
        class: 'p-0 w-16 whitespace-nowrap'
    },
    {
        value: 'actions',
        label: 'Actions',
        class: 'p-0 w-12 whitespace-nowrap'
    },
])
// const defaultOrdering = ref(['-date'])

const inventoryItemsFilter = reactive({
    itemName: {
        name: "item_name",
        label: "Item Name",
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    users: {
        name: "users",
        label: "Users",
        value: [],
        defaultValue: [],
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: setUsersOptions.bind(null),
        awaitingOptions: false,
        visible: true
    },
    category: {
        name: "category",
        label: "Category",
        value: [],
        defaultValue: [],
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: setCategoryOptions.bind(null),
        awaitingOptions: false,
        visible: true
    },
    lifecycle_state: {
        name: "lifecycle_state",
        label: "Lifecycle",
        value: [],
        defaultValue: [],
        options: [
            {value: "clear", label: "------------"},
            {value: "in_stock", label: "Available"},
            {value: "assigned", label: "Assigned"},
            {value: "returned", label: "Needs review"},
            {value: "retired", label: "Retired"},
        ],
        widget: 'list-box',
        multiSelect: true,
        visible: true,
    },
    hide_retired: {
        name: "hide_retired",
        label: "Hide retired",
        value: "true",
        defaultValue: "true",
        options: [
            {value: "true", label: "Hide retired"},
            {value: "false", label: "Show retired"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
    has_open_issues: {
        name: "has_open_issues",
        label: "Has open issues",
        value: null,
        defaultValue: null,
        options: [
            {value: "clear", label: "------------"},
            {value: "true", label: "Yes"},
            {value: "false", label: "No"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
    has_assignment_photos: {
        name: "has_assignment_photos",
        label: "Has assignment photos",
        value: null,
        defaultValue: null,
        options: [
            {value: "clear", label: "------------"},
            {value: "true", label: "Yes"},
            {value: "false", label: "No"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
    open_assignment_only: {
        name: "open_assignment_only",
        label: "Currently assigned",
        value: null,
        defaultValue: null,
        options: [
            {value: "clear", label: "------------"},
            {value: "true", label: "Yes"},
            {value: "false", label: "No"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
    ever_returned: {
        name: "ever_returned",
        label: "Ever returned",
        value: null,
        defaultValue: null,
        options: [
            {value: "clear", label: "------------"},
            {value: "true", label: "Yes"},
            {value: "false", label: "No"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
    brand: {
        name: "brand",
        label: "Brand",
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    condition: {
        name: "condition",
        label: "Condition",
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    location: {
        name: "location",
        label: "Location",
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    age_band: {
        name: "age_band",
        label: "Age band (laptops)",
        value: null,
        defaultValue: null,
        options: [
            {value: "clear", label: "------------"},
            {value: "lt3", label: "<3 years (green)"},
            {value: "3to4", label: "3-4 years (amber)"},
            {value: "ge4", label: "≥4 years (red)"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
    older_than_years: {
        name: "older_than_years",
        label: "Older than (years)",
        value: null,
        defaultValue: null,
        type: 'number',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    issued_on_from: {
        name: "issued_on_from",
        label: "Issued on (from)",
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    issued_on_to: {
        name: "issued_on_to",
        label: "Issued on (to)",
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    returned_on_from: {
        name: "returned_on_from",
        label: "Returned on (from)",
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    returned_on_to: {
        name: "returned_on_to",
        label: "Returned on (to)",
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    purchase_date_from: {
        name: "purchase_date_from",
        label: "Purchase date (from)",
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    purchase_date_to: {
        name: "purchase_date_to",
        label: "Purchase date (to)",
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        visible: true,
    },
    warranty_status: {
        name: "warranty_status",
        label: "Warranty status",
        value: null,
        defaultValue: null,
        options: [
            {value: "clear", label: "------------"},
            {value: "active", label: "Active"},
            {value: "expiring_soon", label: "Expiring soon"},
            {value: "expired", label: "Expired"},
            {value: "unknown", label: "Unknown"},
        ],
        widget: 'list-box',
        multiSelect: false,
        visible: true,
    },
})

function setLoading(){
    isLoading.value = true
}

async function deleteItem(inventoryId){
    const token = store.state.auth.token
    isLoading.value = true
    const result = await deleteInventoryItem(inventoryId, token)
    if(result.status == 200){
        $toast.open({
            message: 'Inventory item deleted',
            type: 'success',
            duration: 3000 
        })
        loadInventoryItems()
    } else {
        $toast.open({
            message: 'Error, could not delete inventory item',
            type: 'error',
            duration: 3000
        })
    }
}

const inventoryItemsBulkOperations = reactive({})

const menuOptions = reactive({
    selectAll: true,
    paginationArrows: true,
    totalResults: true,
    pageSize: true,
    refreshButton: true,
    filters: true,
    bulkOperations: false,
    addFormRows: true,
})

function setFilters(updatedFilters) {
    for (const key of Object.keys(updatedFilters)) {
        if (key in inventoryItemsFilter) {
            inventoryItemsFilter[key] = updatedFilters[key];
        }
    }
}

async function loadInventoryItems(fetchFilter='') {
    isLoading.value = true;
    const token = store.state.auth.token
    response.value = await getInventoryItems(token, fetchFilter);
    isLoading.value = false;
}

async function setFilterOptions(filterName, queryParams='') {
    console.log('setFilterOptions', filterName)
    if(!inventoryItemsFilter[filterName].awaitingOptions){
        if(inventoryItemsFilter[filterName].setOptionsMethod){
            inventoryItemsFilter[filterName].awaitingOptions = true
            inventoryItemsFilter[filterName].setOptionsMethod(queryParams)
            setTimeout(() => {
                inventoryItemsFilter[filterName].awaitingOptions = false;
            }, 30000)
        }
    }
}


function clearFilter(filterName) {
    if(Array.isArray(inventoryItemsFilter[filterName].value)){
        inventoryItemsFilter[filterName].value = []
    } else {
        inventoryItemsFilter[filterName].value = null
    }
}

// async function setCampaignOwnerOptions() {
//     const token = store.state.auth.token
//     const result = await getAllCampaignOwners(token)
//     companyCampaignChangeRequestsFilter.campaignOwner.options = [{value: "clear", label: "------------"}, ...result.clients.map((client) => {
//             return {value: client.id, label: client.client_name}
//         }
//     )]
// }

async function setUsersOptions() {
    const payload = {
        endpoint: '',
        token: store.state.auth.token
    }
    const result = await getUsers(payload)
    const pickable = (result.users || [])
        .filter(u => !u.hide_from_people_pickers)
        .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
    inventoryItemsFilter.users.options = [
        {value: "clear", label: "------------"},
        ...pickable.map((user) => ({value: user.id, label: `${user.first_name} ${user.last_name}`})),
    ]
}

async function setCategoryOptions() {
    const token = store.state.auth.token
    const result = await getInventoryCategories(token)
    console.log('result', result)
    inventoryItemsFilter.category.options = [{value: "clear", label: "------------"}, ...result.data.map((category) => {
            return {value: category.id, label: category.category_name}
            }
        )]
}

// --- Quick-filter preset buttons ---
function applyPresetLaptop() {
    const laptopOpt = inventoryItemsFilter.category.options.find(o => o.label === 'Laptop')
    if (laptopOpt) {
        inventoryItemsFilter.category.value = [laptopOpt.value]
    }
}

function applyPresetAll() {
    inventoryItemsFilter.category.value = []
}

function applyPresetReset() {
    for (const key of Object.keys(inventoryItemsFilter)) {
        const f = inventoryItemsFilter[key]
        // Clone arrays so we don't share the defaultValue reference.
        f.value = Array.isArray(f.defaultValue) ? [...f.defaultValue] : f.defaultValue
    }
    // The page's defined default is Laptop only — restore it.
    applyPresetLaptop()
}

// On initial mount: load category options first so we can default to Laptop,
// then fetch. (TablePage's filter watcher will also re-fetch when the Laptop
// filter is applied, but we kick off the initial fetch directly to avoid an
// empty-state flash.)
(async () => {
    setUsersOptions()
    await setCategoryOptions()
    applyPresetLaptop()
    loadInventoryItems(`?category=${inventoryItemsFilter.category.value[0] || ''}`)
})()

</script>

<style scoped>
/* Match the All Expenses page's typography: text-sm cells with a dark-primary
   navy header bar. The TableHeader/TableCell components are shared across the
   app, so we override via :deep() selectors scoped to this page only. */
:deep(thead th) {
    background-color: #25274D !important;
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    border-color: #25274D !important;
}
:deep(thead th > div) {
    color: white !important;
    background-color: transparent !important;
    font-size: 0.875rem !important;
    border: 0 !important;
    border-radius: 0 !important;
}
:deep(tbody td) {
    font-size: 0.875rem;
}

/* Narrow + center the first column (the ID / select-all column). */
:deep(thead th:first-child) {
    width: 2.5rem !important;
    min-width: 2.5rem !important;
}
:deep(thead th:first-child > div) {
    justify-content: center !important;
    padding-left: 0.5rem !important;
}
:deep(tbody td:first-child > div) {
    justify-content: center !important;
    padding-left: 0 !important;
    width: 2.5rem !important;
}
</style>
